import { useTinyHost } from "@tiny/host-contract";
import React from "react";

import { SegmentView } from "@tiny/api";
import {
  Chip,
  ExpandButton,
  Link,
  TruncateTooltip,
  Typography,
} from "@tiny/components";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useSegmentListView } from "../SegmentList.context";
import { isPredefinedSegment, isSourceSegment } from "../SegmentList.helpers";
import { getSegmentDateBySort } from "./SegmentItemMain.helpers";

interface SegmentItemMainProps {
  item: SegmentView;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

function SegmentItemMain(props: SegmentItemMainProps) {
  const { navigate } = useTinyHost();
  const { item, expanded, onToggleExpand } = props;

  const { mdUp } = useBreakpoints();

  const { pageable } = useSegmentListView();

  // A list filtered to custom segments only ever links into the segment editor, but the
  // same row renders sources (manual, integration, form) when the caller widens the
  // filter — those live under a different route.
  const detailsPath = isSourceSegment(item.segmentType)
    ? `/main/audience/sources/${item.id}`
    : `/main/audience/segments/${item.id}`;

  return (
    <div>
      {mdUp ? (
        <Link
          component={"button" as React.ElementType}
          style={{ display: "block", width: "100%", textAlign: "left" }}
          onClick={() =>
            navigate.to(detailsPath, {
              state: {
                backUrl: {
                  pathname: location.pathname,
                  search: location.search,
                },
              },
            })
          }
        >
          <TruncateTooltip>
            <Typography
              variant="label1"
              color="primary"
              tone="main"
              display="block"
              mb={1}
              noWrap
            >
              {item.name}
            </Typography>
          </TruncateTooltip>
        </Link>
      ) : (
        <TruncateTooltip>
          <Typography
            variant="label1"
            color="grey"
            tone={800}
            display="block"
            mb={1}
            noWrap
          >
            {item.name}
          </Typography>
        </TruncateTooltip>
      )}
      {item.segmentType === "UPLOAD" && (
        <Chip label="Manual" color="purple" size="small" />
      )}
      {item.segmentType === "INTEGRATION" && (
        <Chip label="Integration" color="blue" size="small" />
      )}
      {item.segmentType === "FORM" && (
        <Chip label="Form" color="green" size="small" />
      )}
      {item.segmentType === "CUSTOM" && !item?.aiEnabled && (
        <Chip label="Custom" color="blue" size="small" />
      )}
      {item.segmentType === "CUSTOM" && item?.aiEnabled && (
        <Chip label="AI Generated" color="primary" size="small" />
      )}
      {isPredefinedSegment(item.segmentType) && (
        <Chip label="Predefined" color="purple" size="small" />
      )}
      <Typography variant="body3" color="grey" tone={700}>
        {getSegmentDateBySort(item, pageable.sort)}
      </Typography>
      {!mdUp && onToggleExpand && expanded !== undefined && (
        <ExpandButton expanded={expanded} onClick={onToggleExpand} ml={2} />
      )}
    </div>
  );
}

export default SegmentItemMain;
